import React, { useId, useState } from 'react';

// Global cache of successfully loaded image URLs
const loadedUrls = new Set<string>();

interface ImageWithLoaderProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  spinnerSize?: number;
  fallbackSrc?: string;
  wrapperClassName?: string;
}

export const ImageWithLoader: React.FC<ImageWithLoaderProps> = ({
  spinnerSize = 24,
  fallbackSrc = 'https://via.placeholder.com/300x400/f3f4f6/9ca3af?text=PS',
  wrapperClassName,
  className,
  onError,
  onLoad,
  style,
  src,
  ...props
}) => {
  const gradientId = useId();
  const alreadyCached = !!(src && loadedUrls.has(src));
  const [loading, setLoading] = useState(!alreadyCached);
  const [errored, setErrored] = useState(false);

  return (
    <div className={`relative overflow-hidden ${wrapperClassName || ''}`} style={style}>
      {loading && !errored && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
          <svg className="animate-spin" width={spinnerSize} height={spinnerSize} viewBox="0 0 50 50" fill="none">
            <defs>
              <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#3451D1" />
                <stop offset="40%" stopColor="#4361EE" />
                <stop offset="100%" stopColor="#6B8AFF" />
              </linearGradient>
            </defs>
            <circle cx="25" cy="25" r="20" stroke="#E5E7EB" strokeWidth="4" />
            <path
              d="M25 5 A20 20 0 0 1 45 25"
              stroke={`url(#${gradientId})`}
              strokeWidth="4"
              strokeLinecap="round"
            />
          </svg>
        </div>
      )}
      <img
        {...props}
        src={src}
        className={`${className || ''} w-full h-full`}
        onLoad={(e) => {
          if (src) loadedUrls.add(src);
          setLoading(false);
          onLoad?.(e);
        }}
        onError={(e) => {
          setLoading(false);
          setErrored(true);
          (e.target as HTMLImageElement).src = fallbackSrc;
          onError?.(e);
        }}
      />
    </div>
  );
};
